import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, BadgeCheck, Clock } from "lucide-react";
import { COMPANY, IMAGES } from "@/lib/site-data";
import LogoEmblem from "@/components/LogoEmblem";
import CountUp from "@/components/CountUp";

const ease = [0.22, 1, 0.36, 1];
const D =
  typeof window !== "undefined" && window.sessionStorage.getItem("nb_intro_seen") ? 0 : 1.9;
const LINES = [
  { text: "L’art de la", accent: false },
  { text: "couverture", accent: true },
  { text: "d’exception.", accent: false },
];
const BADGES = [
  { icon: ShieldCheck, label: "Garantie décennale" },
  { icon: Clock, label: "Urgence 24/7" },
];
const STATS = [
  { value: "25+", label: "années de métier" },
  { value: "596", label: "travaux effectués" },
  { value: "4,9/5", label: "avis clients" },
];

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.35]);

  return (
    <section id="accueil" data-testid="hero-section" className="relative flex min-h-screen flex-col justify-center overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Toits de Bordeaux" className="h-[115%] w-full object-cover" />
        <div className="absolute inset-0 bg-[#0D0F12]/55" />
      </motion.div>
      <div className="grain pointer-events-none absolute inset-0" />

      <motion.div style={{ opacity }} className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-36">
        <motion.div
          initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: D + 0.15, duration: 0.8, ease }}
          className="mb-6 md:hidden"
          data-testid="hero-logo-mobile"
        >
          <LogoEmblem size={96} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 0.25, duration: 0.7, ease }}
          data-testid="hero-eyebrow"
          className="font-mono text-xs uppercase tracking-[0.35em] text-terra"
        >
          Couvreur · Zingueur — Bordeaux & Gironde
        </motion.p>

        <div className="flex items-center gap-10 lg:gap-16">
          <h1 className="mt-7 font-serif text-5xl font-semibold leading-[1.04] tracking-tight text-white sm:text-7xl lg:text-8xl">
            {LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: D + 0.45 + i * 0.15, duration: 0.95, ease }}
                  className={`block ${l.accent ? "italic text-terra" : ""}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: D + 0.9, duration: 1, ease }}
            className="hidden shrink-0 md:block"
            data-testid="hero-logo"
          >
            <motion.div
              animate={{ y: [0, -9, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
            >
              <LogoEmblem size={200} />
            </motion.div>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 1.05, duration: 0.7, ease }}
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg"
        >
          Depuis 1999, nous rénovons, réparons et isolons les toitures de la Gironde avec des matériaux
          nobles et un savoir-faire de maître artisan.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 1.2, duration: 0.7, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#devis"
            data-testid="hero-devis-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-terra px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terra-hover"
          >
            Demander un devis gratuit
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href={COMPANY.phoneHref}
            data-testid="hero-phone-cta"
            className="inline-flex items-center gap-3 rounded-full border border-white/15 px-7 py-4 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:border-terra/60 hover:text-white"
          >
            <Phone className="h-4 w-4 text-terra" />
            Urgence fuite 24/7
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: D + 1.35, duration: 0.7, ease }}
          className="mt-12 flex flex-wrap gap-3"
        >
          {BADGES.map((b) => (
            <span
              key={b.label}
              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-300 backdrop-blur"
            >
              <b.icon className="h-3.5 w-3.5 text-terra" />
              {b.label}
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: D + 1.55, duration: 0.9 }}
          className="mt-14 grid max-w-2xl grid-cols-3 gap-6 rounded border border-white/10 bg-black/50 px-5 py-6 backdrop-blur-md md:px-8"
        >
          {STATS.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.replace(/\s/g, "-")}`}>
              <p className="font-serif text-4xl font-bold text-white [text-shadow:0_2px_16px_rgba(0,0,0,1)] md:text-5xl">
                <CountUp value={s.value} startDelay={D + 1.6} />
              </p>
              <p className="mt-2 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white [text-shadow:0_1px_10px_rgba(0,0,0,1)] md:text-sm">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
